// libs
import { History } from 'history'
import { kebabCase } from 'lodash'
import { match as MatchType } from 'react-router-dom'

// interfaces / constants
import { PostResponseType } from 'src/api/interfaces/responses/post'
import {
  ADMIN_PROFILE_TYPE,
  AUTHORITY_PROFILE_TYPE,
  BLOG_PROFILE_TYPE,
  INTERMEDIATE_PROFILE_TYPE,
  PRESS_PROFILE_TYPE,
  PRIVATE_PROFILE_TYPE,
  REPORTER_PROFILE_TYPE
} from 'src/constants/profile'
import { SELECT_PATH, WRITE_PATH } from 'src/constants/route_constants'
import * as URLS from 'src/constants/urls'
import { getProjectRoutes } from 'src/constants/urls'
import { ILopoCore } from 'src/interfaces'
import { IContentItemType } from 'src/interfaces/posts'
import { ProfileType, ProfilesListProfileType } from 'src/interfaces/profile'
import { ProfilesListURI } from 'src/interfaces/profiles_list'
import { getConfigValue } from 'src/utils/config/config'
import { REGEXP_LOCK_MAP_PAGE } from 'src/utils/pattern/pattern'

// helpers
import { ActorProfilePreviewFragmentFragment, ActorProfileType, ContentItemFragmentFragment, ProjectType } from '@lopo-tech/lopo-api'
import { PROFILE_LIST_URIS_TO_AUTHOR_TYPE } from 'src/constants/profile_list'
import { reportError } from 'src/utils/reporting/report-errors'
import { idFromGlobalFriendlyIdentifier } from '../profile'

export interface IProfileParams {
  profileType?: string;
  profileId?: string;
}

interface IProfileData {
  profileType?: ActorProfileType;
  slug?: string;
}

export interface IGroupsParams {
  id: string;
}

export const getProfileDataFromUrlParams = (params: IProfileParams): IProfileData => {
  const profileType = (params.profileType?.toLocaleUpperCase() +
    '_PROFILE') as ActorProfileType
  const profileId = params.profileId?.toLowerCase()
  return {
    slug: profileId,
    profileType
  }
}

const getNewsFeedPathWithSlug = (locationSlug: string) => `/${locationSlug}${URLS.NEWS_FEED_PARTIAL}`

const replaceAtIndex = (array: string[], value: string, index: number) =>
  [...array.slice(0, index), value, ...array.slice(index + 1)]

// to replace the locationSlug no matter where in the url
// for example loaklportal.de/nachbarschaft/[shape_slug] or
// loaklportal.de/[shape_slug]/nachbarschaft or any other
export const replaceLocationSlugFromPath = (match: MatchType | null, locationSlug: string) => {
  const customLocationSlug = getConfigValue<string>('featureFlags.location.customLocationSlug')
  if (customLocationSlug && match) {
    return match.url
  }
  const path = match?.path || ''
  const url = match?.url || ''
  // The path pattern used to match this route (the one passed to the Route that matches)
  // can look like /:locationSlug/aktuelles
  const pathArray = path.split('/')
  //  The matched portion of the URL. looks the like
  //  /kiel-ravensberg/aktuelles
  const urlArray = url.split('/')
  const index = pathArray.indexOf(URLS.LOCATION_SLUG_PATTERN)

  if (index < 0) {
    // defauts to neswsfeed if no slug could be replaced
    return getNewsFeedPathWithSlug(locationSlug)
  }

  return replaceAtIndex(urlArray, locationSlug, index).join('/')
}

/*
   Use of parseQueryString
   const myObj = parseQueryString('ksks?a=2', ['a', 'b']);
   myObj.c; // error
   myObj.b; // works!
  */
export const parseQueryString = <T extends string, R = {[key in T]?: string}>(search: string, wishedKeys: T[]): R => {
  const querystring = new URLSearchParams(search)

  return wishedKeys.reduce(
    (prevVal, currVal: T) => {
      const querystringVal = querystring.get(currVal)

      if (querystringVal === null) {
        return prevVal
      }

      return { ...prevVal, [currVal]: querystringVal }
    }, {}) as R
}

// The match is case-insensitive on purpose: without the flag a target that
// already carries an allowed scheme in capitals, such as HTTPS://example.de, is
// not recognised as having one and gets a second scheme glued in front of it.
export const addSchemaIfNotPresent = (urlLike: string) =>
  !urlLike.match(/^https?:\/\//i) ? `http://${urlLike}` : urlLike

export const getCreatePathFrom = (path: string) =>
  path.includes(URLS.CREATE_POST) ? path : path + URLS.CREATE_POST

export class UrlUtils {
  public static projectDetailPage (
    identifier: string,
    projectType: ProjectType
  ): string {
    return getProjectRoutes(projectType).detail(identifier)
  }

  public static groupPage (identifier: string): string {
    return `${URLS.FRONTEND_GROUP_PATH}${identifier}`
  }

  public static groupMembersPage (identifier: string): string {
    return `${URLS.FRONTEND_GROUP_PATH}${identifier}/members`
  }

  public static detailPage (
    identifier: string,
    type: PostResponseType | ContentItemFragmentFragment['__typename']
  ): string {
    // TODO: remove PostResponseType as soon as rest->gql migration is done
    switch (type) {
      case 'post':
        return `${URLS.FRONTEND_POST_PATH}/${identifier}`
      case 'Article':
        return `${URLS.FRONTEND_ARTICLE_PATH}/${identifier}`
      case 'Snapshot':
        return `${URLS.FRONTEND_SNAPSHOT_PATH}/${identifier}`
      case 'event':
      case 'Event':
        return `${URLS.FRONTEND_EVENT_PATH}/${identifier}`
    }
  }

  public static getProfileFrontendPath (
    identifier: string,
    profileType: ProfileType | ActorProfilePreviewFragmentFragment['__typename']
  ): string {
    if (!identifier || !profileType) {
      return ''
    }

    const id = idFromGlobalFriendlyIdentifier(identifier)
    switch (profileType) {
      // intermediate profiles got no profile detail page
      case INTERMEDIATE_PROFILE_TYPE:
      case ADMIN_PROFILE_TYPE:
        return ''

      case 'PrivateProfile':
      case PRIVATE_PROFILE_TYPE:
        return URLS.FRONTEND_PRIVATE_PROFILE_FEED_PATH + id

      case 'BlogProfile':
      case BLOG_PROFILE_TYPE:
        return URLS.FRONTEND_BLOG_PROFILE_FEED_PATH + id

      case 'PressProfile':
      case PRESS_PROFILE_TYPE:
        return URLS.FRONTEND_PRESS_PROFILE_FEED_PATH + id

      case 'AuthorityProfile':
      case AUTHORITY_PROFILE_TYPE:
        return URLS.FRONTEND_AUTHORITY_PROFILE_FEED_PATH + id

      case REPORTER_PROFILE_TYPE:
        return URLS.FRONTEND_REPORTER_PROFILE_FEED_PATH + id

      default:
        reportError('unknown profileType in UrlUtils.getProfileFrontendPath!', {
          identifier,
          profileType
        })
        return ''
    }
  }

  public static mapUrlParamToAuthorType (
    uri: ProfilesListURI
  ): ProfilesListProfileType {
    return PROFILE_LIST_URIS_TO_AUTHOR_TYPE[uri] as ProfilesListProfileType
  }

  public static conversationsPage (): string {
    return `${URLS.FRONTEND_CONVERSATION_PATH}`
  }

  public static conversationPage (id: string): string {
    return `${URLS.FRONTEND_CONVERSATION_PATH}${id}/`
  }

  public static sendMessageConversationPage (type: ActorProfilePreviewFragmentFragment['__typename'], id: string): string {
    return `${
      URLS.FRONTEND_SEND_MESSAGE_CONVERSATION_PATH
    }${type}/${idFromGlobalFriendlyIdentifier(id)}`
  }

  public static isLockMapUrl (pathname: string) {
    return pathname.match(REGEXP_LOCK_MAP_PAGE)
  }

  // returns the current url with only host and pathname.
  public static currentUrlWithHostAndPath (): string {
    return this.canonicalUrl(window.location.href)
  }

  // returns the current url with host and pathname as well as whitelisted parameters
  public static currentUrlWithHostAndPathAndFilteredParams (): string {
    return this.filterSearchParams(window.location.href)
  }

  // returns an URL with only origin and pathname
  public static canonicalUrl (url: string): string {
    const urlObject = new URL(url)
    return urlObject.origin + urlObject.pathname
  }

  // returns the value of the current URL search params for the given key, if present
  public static getCurrentUrlSearchParam (key: string): string | null {
    return new URLSearchParams(window.location.search).get(key)
  }

  public static getCurrentUrlPath (): string {
    return window.location.pathname
  }

  public static getCurrentUrlOrigin (): string {
    if (!window.location.origin) {
      const port = window.location.port ? ':' + window.location.port : ''
      return `${window.location.protocol}//${window.location.hostname}${port}`
    } else {
      return window.location.origin
    }
  }

  /**
   * universal goBack logic
   */
  public static goBack (history: History, goto?: string) {
    const currentURL = window.location.href
    if (!(window as unknown as ILopoCore).lopo_core.isFirstNavigationPage) {
      history.goBack()
    }

    setTimeout(() => {
      if (window.location.href === currentURL) {
        if (goto) {
          history.push(goto)
          return
        }
        history.push('/')
      }
    }, 100)
  }

  public static setLocationHref (href: string): void {
    window.location.href = href
  }

  // unlike setLocationHref, the current page does not stay in the browser history
  public static replaceLocationHref (href: string): void {
    window.location.replace(href)
  }

  public static emailConfirmationRedirectUrl (): string {
    return (
      this.getCurrentUrlOrigin() +
      URLS.REDIRECT_URL_REGISTRATION_EMAIL_CONFIRMATION
    )
  }

  public static emailConfirmationRedirectUrlForPublicProfile (): string {
    return (
      this.getCurrentUrlOrigin() +
      URLS.REDIRECT_URL_REGISTRATION_EMAIL_CONFIRMATION_PUBLIC_PROFILE
    )
  }

  private static WHITELISTED_URL_PARAMS = [
    'pt' // preview token, we need to keep this to allow previewing an unpublished post on Facebook
  ]

  private static filterSearchParams (url: string): string {
    const urlObject = new URL(url)
    const filteredParams = new URLSearchParams()

    this.WHITELISTED_URL_PARAMS.forEach((whitelistedParam) => {
      if (urlObject.searchParams.has(whitelistedParam)) {
        filteredParams.append(
          whitelistedParam,
          urlObject.searchParams.get(whitelistedParam) as string
        )
      }
    })
    urlObject.search = filteredParams.toString()

    return urlObject.origin + urlObject.pathname + urlObject.search
  }

  public static getPostCreateURL (
    matchUrl: string,
    search: string,
    forceWizard: boolean,
    postType?: IContentItemType
  ) {
    if (postType) {
      if (forceWizard) {
        return (
          getCreatePathFrom(matchUrl) +
          `/${kebabCase(postType)}${SELECT_PATH}${search}`
        )
      }
      return (
        getCreatePathFrom(matchUrl) +
        `/${kebabCase(postType)}${WRITE_PATH}${search}`
      )
    }
    return getCreatePathFrom(matchUrl) + SELECT_PATH + search
  }
}
